/** @format */

import { DeleteOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Drawer, Empty, Input, Popconfirm, Tabs, Typography } from "antd";
import React, { useState } from "react";
import {
  apiOrganigrammaRoleCreate,
  apiOrganigrammaRoleDelete,
  apiOrganigrammaRoleUpdate,
  apiOrganigrammaStructureCreate,
  apiOrganigrammaStructureDelete,
  apiOrganigrammaStructureUpdate,
  organigrammaErrorMessage,
} from "../../../../api/apiOrganigramma";
import { TOrgRole, TOrgStructure } from "../../../../redux-modules/declarations/maps/organigramma";
import { showNotification } from "../../../../utils/showNotification";
import { nextStructureColors } from "../utils/people";
import { StructureColorPicker } from "./StructureColorPicker";

export type TCatalogTab = "roles" | "structures";

interface Props {
  open: boolean;
  tab: TCatalogTab;
  onTabChange: (tab: TCatalogTab) => void;
  onClose: () => void;
  roles: TOrgRole[];
  structures: TOrgStructure[];
  canUpdate: boolean;
  /** ricarica organigramma e liste dopo una modifica */
  onChanged: () => Promise<void>;
  /** evidenzia nel grafico le persone con il ruolo */
  onShowRole: (role: TOrgRole) => void;
  /** evidenzia nel grafico la struttura */
  onShowStructure: (structure: TOrgStructure) => void;
}

const peopleLabel = (count: number): string =>
  count === 0 ? "Nessuna persona" : count === 1 ? "1 persona" : `${count} persone`;

/** Campo unico "cerca o aggiungi": filtra l'elenco e, se il nome non esiste, lo crea. */
const SearchOrAdd = ({
  placeholder,
  value,
  onChange,
  canAdd,
  adding,
  onAdd,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  canAdd: boolean;
  adding: boolean;
  onAdd: () => void;
}): JSX.Element => (
  <div className="org-catalog__add">
    <Input
      allowClear
      prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.35)" }} />}
      placeholder={placeholder}
      value={value}
      onChange={(e): void => onChange(e.target.value)}
      onPressEnter={(): void => {
        if (canAdd) onAdd();
      }}
    />
    <Button type="primary" icon={<PlusOutlined />} disabled={!canAdd} loading={adding} onClick={onAdd}>
      Aggiungi
    </Button>
  </div>
);

export const CatalogDrawer = ({
  open,
  tab,
  onTabChange,
  onClose,
  roles,
  structures,
  canUpdate,
  onChanged,
  onShowRole,
  onShowStructure,
}: Props): JSX.Element => {
  const [roleQuery, setRoleQuery] = useState("");
  const [structureQuery, setStructureQuery] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (key: string, action: () => Promise<unknown>, success: string): Promise<boolean> => {
    setBusy(key);
    try {
      await action();
      await onChanged();
      showNotification("success", "Organigramma", success);
      return true;
    } catch (err) {
      showNotification("error", "Operazione non riuscita", organigrammaErrorMessage(err, "Riprova."));
      return false;
    } finally {
      setBusy(null);
    }
  };

  // ---- Ruoli -------------------------------------------------------------
  const roleName = roleQuery.trim();
  const roleExists = roles.some(r => r.name.trim().toLowerCase() === roleName.toLowerCase());
  const filteredRoles = roleName
    ? roles.filter(r => r.name.toLowerCase().includes(roleName.toLowerCase()))
    : roles;

  const addRole = async (): Promise<void> => {
    if (!roleName || roleExists) return;
    const ok = await run("role-new", () => apiOrganigrammaRoleCreate(roleName), `Ruolo «${roleName}» creato.`);
    if (ok) setRoleQuery("");
  };

  // ---- Strutture -----------------------------------------------------------
  const structureName = structureQuery.trim();
  const structureExists = structures.some(
    s => s.name.trim().toLowerCase() === structureName.toLowerCase(),
  );
  const filteredStructures = structureName
    ? structures.filter(s => s.name.toLowerCase().includes(structureName.toLowerCase()))
    : structures;

  const addStructure = async (): Promise<void> => {
    if (!structureName || structureExists) return;
    const ok = await run(
      "structure-new",
      () => apiOrganigrammaStructureCreate({ name: structureName, ...nextStructureColors(structures) }),
      `Struttura «${structureName}» creata.`,
    );
    if (ok) setStructureQuery("");
  };

  const updateStructure = (structure: TOrgStructure, patch: Partial<TOrgStructure>): void => {
    const next = { ...structure, ...patch };
    run(
      `structure-${structure.id}`,
      () =>
        apiOrganigrammaStructureUpdate(structure.id, {
          name: next.name,
          background_color: next.background_color,
          text_color: next.text_color,
        }),
      "Struttura aggiornata.",
    );
  };

  const rolesTab = (
    <div className="org-catalog">
      {canUpdate && (
        <SearchOrAdd
          placeholder="Cerca o aggiungi un ruolo"
          value={roleQuery}
          onChange={setRoleQuery}
          canAdd={Boolean(roleName) && !roleExists}
          adding={busy === "role-new"}
          onAdd={addRole}
        />
      )}
      {filteredRoles.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={roleName ? `Nessun ruolo «${roleName}»: premi Aggiungi per crearlo.` : "Nessun ruolo."}
        />
      ) : (
        <ul className="org-catalog__list">
          {filteredRoles.map(role => (
            <li key={role.id} className="org-catalog__row">
              <Typography.Text
                className="org-catalog__name"
                editable={
                  canUpdate
                    ? {
                        tooltip: "Rinomina",
                        onChange: (name: string): void => {
                          const clean = name.trim();
                          if (clean && clean !== role.name)
                            run(`role-${role.id}`, () => apiOrganigrammaRoleUpdate(role.id, clean), "Ruolo rinominato.");
                        },
                      }
                    : false
                }
              >
                {role.name}
              </Typography.Text>
              <Button
                type="link"
                size="small"
                disabled={!role.users_count}
                onClick={(): void => onShowRole(role)}
                title="Evidenzia nel grafico"
              >
                {peopleLabel(role.users_count)}
              </Button>
              {canUpdate && (
                <Popconfirm
                  placement="left"
                  okText="Elimina"
                  okButtonProps={{ danger: true }}
                  cancelText="Annulla"
                  title={
                    <div className="org-confirm">
                      <strong>Eliminare il ruolo «{role.name}»?</strong>
                      {role.users_count > 0 && (
                        <div>
                          {role.users_count === 1
                            ? "La persona con questo ruolo verrà tolta dall'organigramma."
                            : `Le ${role.users_count} persone con questo ruolo verranno tolte dall'organigramma.`}
                        </div>
                      )}
                    </div>
                  }
                  onConfirm={(): void => {
                    run(`role-${role.id}`, () => apiOrganigrammaRoleDelete(role.id), `Ruolo «${role.name}» eliminato.`);
                  }}
                >
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    loading={busy === `role-${role.id}`}
                    aria-label={`Elimina il ruolo ${role.name}`}
                  />
                </Popconfirm>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const structuresTab = (
    <div className="org-catalog">
      {canUpdate && (
        <SearchOrAdd
          placeholder="Cerca o aggiungi una struttura"
          value={structureQuery}
          onChange={setStructureQuery}
          canAdd={Boolean(structureName) && !structureExists}
          adding={busy === "structure-new"}
          onAdd={addStructure}
        />
      )}
      {filteredStructures.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            structureName ? `Nessuna struttura «${structureName}»: premi Aggiungi per crearla.` : "Nessuna struttura."
          }
        />
      ) : (
        <ul className="org-catalog__list">
          {filteredStructures.map(structure => (
            <li key={structure.id} className="org-catalog__row">
              <span
                className="org-catalog__chip"
                style={{
                  background: structure.background_color,
                  color: structure.text_color,
                  borderColor: structure.text_color,
                }}
              >
                <Typography.Text
                  style={{ color: "inherit" }}
                  editable={
                    canUpdate
                      ? {
                          tooltip: "Rinomina",
                          onChange: (name: string): void => {
                            const clean = name.trim();
                            if (clean && clean !== structure.name) updateStructure(structure, { name: clean });
                          },
                        }
                      : false
                  }
                >
                  {structure.name}
                </Typography.Text>
              </span>
              <Button
                type="link"
                size="small"
                disabled={!structure.users_count}
                onClick={(): void => onShowStructure(structure)}
                title="Evidenzia nel grafico"
              >
                {peopleLabel(structure.users_count)}
              </Button>
              {canUpdate && (
                <StructureColorPicker
                  value={{ background_color: structure.background_color, text_color: structure.text_color }}
                  onChange={(colors): void => updateStructure(structure, colors)}
                  disabled={busy === `structure-${structure.id}`}
                />
              )}
              {canUpdate && (
                <Popconfirm
                  placement="left"
                  okText="Elimina"
                  okButtonProps={{ danger: true }}
                  cancelText="Annulla"
                  title={
                    <div className="org-confirm">
                      <strong>Eliminare la struttura «{structure.name}»?</strong>
                      {structure.users_count > 0 && (
                        <div>
                          {structure.users_count === 1
                            ? "La persona resterà nell'organigramma, senza struttura."
                            : `Le ${structure.users_count} persone resteranno nell'organigramma, senza struttura.`}
                        </div>
                      )}
                    </div>
                  }
                  onConfirm={(): void => {
                    run(
                      `structure-${structure.id}`,
                      () => apiOrganigrammaStructureDelete(structure.id),
                      `Struttura «${structure.name}» eliminata.`,
                    );
                  }}
                >
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    loading={busy === `structure-${structure.id}`}
                    aria-label={`Elimina la struttura ${structure.name}`}
                  />
                </Popconfirm>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <Drawer
      title="Ruoli e strutture"
      placement="right"
      width={480}
      visible={open}
      onClose={onClose}
      className="org-drawer"
      bodyStyle={{ paddingTop: 8 }}
    >
      <Tabs activeKey={tab} onChange={(key): void => onTabChange(key as TCatalogTab)}>
        <Tabs.TabPane key="roles" tab={`Ruoli (${roles.length})`}>
          {rolesTab}
        </Tabs.TabPane>
        <Tabs.TabPane key="structures" tab={`Strutture (${structures.length})`}>
          {structuresTab}
        </Tabs.TabPane>
      </Tabs>
    </Drawer>
  );
};
